// -----------------------------------------------------------------------------
// API client for the sector-template catalog (Phase 8 Wave 8.E).
//
// The catalog is global readonly; the tenant only browses and instantiates.
// Instantiation goes through `POST /templates/{id}/instantiate` and returns the
// freshly-created DocumentType draft response so the caller can route to the
// editor without a separate fetch.
// -----------------------------------------------------------------------------

import { apiFetch } from '../../../api/client';
import { Endpoints } from '../../../api/types';
import type { DocumentTypeResponse } from '../../schema/api';

const TEMPLATES_BASE = `${Endpoints.documentTypes}/templates`;

/** Mirrors `Sector` enum on the server. */
export type SectorName = 'Generic' | 'Logistics' | 'Finance' | 'Ecommerce' | 'Healthcare';

export interface SectorTemplateSummaryResponse {
  id: string;
  sector: SectorName;
  /** Localized (es-ES) sector label for display; `sector` stays the grouping key. */
  sectorLabel: string;
  locale: string;
  name: string;
  description: string;
  fieldCount: number;
  /** First few field names, for an at-a-glance preview on the card. */
  previewFields: string[];
}

export interface SectorTemplateFieldPreview {
  name: string;
  type: string;
  required: boolean;
  critical: boolean;
}

export interface SectorTemplateThresholds {
  fieldWarning: number;
  fieldCritical: number;
  documentReviewRatio: number;
  documentRejectGlobalScore: number;
}

export interface SectorTemplateDetailResponse {
  id: string;
  sector: SectorName;
  locale: string;
  name: string;
  description: string;
  fields: SectorTemplateFieldPreview[];
  thresholds: SectorTemplateThresholds;
}

export interface InstantiateSectorTemplateBody {
  modelProviderId: string;
  overrideName?: string;
  overrideSlug?: string;
}

/** GET /document-types/templates?sector=&q= */
export function listSectorTemplates(
  sector?: SectorName,
  q?: string,
  signal?: AbortSignal,
): Promise<SectorTemplateSummaryResponse[]> {
  const params = new URLSearchParams();
  if (sector !== undefined) {
    params.set('sector', sector);
  }
  if (q !== undefined && q.trim() !== '') {
    params.set('q', q.trim());
  }
  const qs = params.toString();
  const url = qs === '' ? TEMPLATES_BASE : `${TEMPLATES_BASE}?${qs}`;
  return apiFetch<SectorTemplateSummaryResponse[]>(url, signal !== undefined ? { signal } : {});
}

/** GET /document-types/templates/{id} */
export function getSectorTemplate(
  id: string,
  signal?: AbortSignal,
): Promise<SectorTemplateDetailResponse> {
  return apiFetch<SectorTemplateDetailResponse>(
    `${TEMPLATES_BASE}/${encodeURIComponent(id)}`,
    signal !== undefined ? { signal } : {},
  );
}

/** POST /document-types/templates/{id}/instantiate */
export function instantiateSectorTemplate(
  id: string,
  body: InstantiateSectorTemplateBody,
): Promise<DocumentTypeResponse> {
  return apiFetch<DocumentTypeResponse>(
    `${TEMPLATES_BASE}/${encodeURIComponent(id)}/instantiate`,
    {
      method: 'POST',
      json: body,
    },
  );
}

export const SectorTemplateQueryKeys = {
  list: (sector?: SectorName, q?: string): readonly unknown[] => ['sector-templates', sector ?? '__all__', q ?? ''],
  detail: (id: string): readonly unknown[] => ['sector-templates', 'detail', id],
} as const;
